import { useState, useEffect, useCallback } from 'react';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  limit, 
  startAfter,
  getDocs, 
  doc, 
  getDoc,
  addDoc, 
  updateDoc, 
  deleteDoc,
  serverTimestamp,
  Timestamp,
  getCountFromServer
} from 'firebase/firestore';
import { db } from '../firebase';
import { generateUniqueSlug, reserveSlug, releaseSlug, updateSlugReservation } from '../utils/slugUtils';
import { sanitizeHTML, generateExcerpt, validateContent } from '../utils/contentSanitizer';

/**
 * Custom hook for managing blog posts
 */
export const useBlogPosts = () => {
  const [posts, setPosts] = useState([]);
  const [post, setPost] = useState(null); // Single post state
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetches blog posts with optional filters and pagination
   */
  const fetchPosts = useCallback(async (filters = {}) => {
    setLoading(true);
    setError(null);

    try {
      let q = collection(db, 'blog_posts');
      const conditions = [];

      // Status filter
      if (filters.status) {
        conditions.push(where('status', '==', filters.status));
      }

      // Published filter for public access - includes scheduled posts that should be live
      if (filters.published === true) {
        // Get posts that are either published OR scheduled with a past publish date
        conditions.push(where('status', 'in', ['published', 'scheduled']));
      }

      // Category filter
      if (filters.category) {
        conditions.push(where('categories', 'array-contains', filters.category));
      }

      // Apply filters
      if (conditions.length > 0) {
        q = query(q, ...conditions);
      }

      // Ordering
      const orderByField = filters.orderBy || 'createdAt';
      const orderDirection = filters.orderDirection || 'desc';
      q = query(q, orderBy(orderByField, orderDirection));

      // Pagination: only use startAfter if a valid DocumentSnapshot is provided
      if (filters.startAfter && typeof filters.startAfter === 'object' && typeof filters.startAfter.exists === 'function' && filters.startAfter.exists()) {
        q = query(q, startAfter(filters.startAfter));
      }

      const limitCount = filters.limit || 10;
      q = query(q, limit(limitCount));

      const snapshot = await getDocs(q);
      let fetchedPosts = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        // Convert Firestore timestamps to JavaScript dates for easier handling
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
        publishDate: doc.data().publishDate?.toDate(),
        publishedAt: doc.data().publishedAt?.toDate(),
        _docSnap: doc // Attach the DocumentSnapshot for pagination
      }));

      // For public access, filter out scheduled posts that shouldn't be shown yet
      if (filters.published === true) {
        const now = new Date();
        fetchedPosts = fetchedPosts.filter(post => {
          if (post.status === 'published') return true;
          if (post.status === 'scheduled' && post.publishDate) {
            return post.publishDate <= now;
          }
          return false;
        });
      }

      // If this is a fresh fetch (no startAfter), replace posts
      // Otherwise, append for pagination
      if (!filters.startAfter) {
        setPosts(fetchedPosts);
      } else {
        setPosts(prev => [...prev, ...fetchedPosts]);
      }

      return {
        posts: fetchedPosts,
        hasMore: fetchedPosts.length === limitCount,
        lastDoc: snapshot.docs[snapshot.docs.length - 1] || null
      };

    } catch (err) {
      console.error('Error fetching posts:', err);
      setError('Failed to fetch posts');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fetches a single blog post by ID or slug
   */
  const fetchPost = useCallback(async (identifier, isSlug = false) => {
    setLoading(true);
    setError(null);
    setPost(null);
    
    try {
      let postDoc;
      
      if (isSlug) {
        const slugDoc = await getDoc(doc(db, 'blog_slugs', identifier));
        if (slugDoc.exists()) {
          const postId = slugDoc.data().postId;
          postDoc = await getDoc(doc(db, 'blog_posts', postId));
        } else {
          // Fallback: query blog_posts directly by slug field
          // (handles posts created server-side without a slug registry entry)
          const fallbackQ = query(collection(db, 'blog_posts'), where('slug', '==', identifier), limit(1));
          const fallbackSnap = await getDocs(fallbackQ);
          if (fallbackSnap.empty) {
            throw new Error('Post not found');
          }
          postDoc = fallbackSnap.docs[0];
        }
      } else {
        postDoc = await getDoc(doc(db, 'blog_posts', identifier));
      }
      
      if (!postDoc.exists()) {
        throw new Error('Post not found');
      }
      
      const post = {
        id: postDoc.id,
        ...postDoc.data(),
        createdAt: postDoc.data().createdAt?.toDate(),
        updatedAt: postDoc.data().updatedAt?.toDate(),
        publishDate: postDoc.data().publishDate?.toDate(),
        publishedAt: postDoc.data().publishedAt?.toDate()
      };
      
      setPost(post);
      return post;
      
    } catch (err) {
      console.error('useBlogPosts: Error fetching post:', err);
      setError('Failed to fetch post');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Creates a new blog post
   */
  const createPost = async (postData, authorData) => {
    setLoading(true);
    setError(null);
    
    try {
      // Validate content
      const contentValidation = validateContent(postData.content);
      if (!contentValidation.isValid) {
        throw new Error(contentValidation.errors.join(', '));
      }
      
      // Generate unique slug
      const slug = await generateUniqueSlug(postData.title);
      
      // Sanitize content
      const sanitizedContent = sanitizeHTML(postData.content);
      
      // Generate excerpt if not provided
      const excerpt = postData.excerpt || generateExcerpt(sanitizedContent);
      
      // Prepare post data
      const newPost = {
        title: postData.title,
        slug: slug,
        content: sanitizedContent,
        rawContent: postData.rawContent ? JSON.stringify(postData.rawContent) : null, // Convert Delta to string for Firestore
        excerpt: excerpt,
        author: {
          id: authorData.uid,
          name: postData.authorName || authorData.displayName || authorData.name || authorData.email,
          email: authorData.email
        },
        status: postData.status || 'draft',
        published: postData.status === 'published',
        categories: postData.categories || [],
        tags: postData.tags || [],
        featuredImage: postData.featuredImage || null,
        seo: {
          metaTitle: postData.seo?.metaTitle || postData.title,
          metaDescription: postData.seo?.metaDescription || excerpt,
          keywords: postData.seo?.keywords || []
        },
        searchable: true,
        analytics: {
          views: 0,
          likes: 0,
          shares: 0
        },
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      // Handle scheduled posts and automatic scheduling
      if (postData.status === 'scheduled' && postData.publishDate) {
        const publishDate = new Date(postData.publishDate);
        newPost.publishDate = Timestamp.fromDate(publishDate);
        // Auto-schedule if publish date is in future but status says published
      } else if (postData.status === 'published' && postData.publishDate) {
        const publishDate = new Date(postData.publishDate);
        const now = new Date();
        if (publishDate > now) {
          // Future date with published status = scheduled
          newPost.status = 'scheduled';
          newPost.published = false;
          newPost.publishDate = Timestamp.fromDate(publishDate);
        } else {
          // Past or current date with published status = publish now
          newPost.publishedAt = serverTimestamp();
        }
      } else if (postData.status === 'published') {
        newPost.publishedAt = serverTimestamp();
      }
      
      // Create the post document
      const docRef = await addDoc(collection(db, 'blog_posts'), newPost);
      
      // Reserve the slug
      await reserveSlug(slug, docRef.id);
      
      // Update local state
      const createdPost = { id: docRef.id, ...newPost };
      setPosts(prev => [createdPost, ...prev]);
      
      return createdPost;
      
    } catch (err) {
      console.error('Error creating post:', err);
      setError('Failed to create post');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Updates an existing blog post
   */
  const updatePost = async (postId, postData, authorData) => {
    setLoading(true);
    setError(null);
    
    try {
      // Get the current post to check for slug changes
      const currentPost = await fetchPost(postId);
      const oldSlug = currentPost.slug;
      
      // Validate content if provided
      if (postData.content) {
        const contentValidation = validateContent(postData.content);
        if (!contentValidation.isValid) {
          throw new Error(contentValidation.errors.join(', '));
        }
      }
      
      // Generate new slug if title changed
      let newSlug = oldSlug;
      if (postData.title && postData.title !== currentPost.title) {
        newSlug = await generateUniqueSlug(postData.title, postId);
      }
      
      // Prepare update data
      const updateData = {
        updatedAt: serverTimestamp()
      };
      
      // Update fields if provided
      if (postData.title) updateData.title = postData.title;
      if (newSlug !== oldSlug) updateData.slug = newSlug;
      
      if (postData.content) {
        updateData.content = sanitizeHTML(postData.content);
        updateData.rawContent = postData.rawContent ? JSON.stringify(postData.rawContent) : null;
      }
      
      if (postData.excerpt) {
        updateData.excerpt = postData.excerpt;
      } else if (postData.content) {
        updateData.excerpt = generateExcerpt(sanitizeHTML(postData.content));
      }
      
      if (postData.status) {
        updateData.status = postData.status;
        updateData.published = postData.status === 'published';
        
        // Handle publishing
        if (postData.status === 'published' && currentPost.status !== 'published') {
          updateData.publishedAt = serverTimestamp();
        }
        
        // Handle scheduled posts
        if (postData.status === 'scheduled' && postData.publishDate) {
          updateData.publishDate = Timestamp.fromDate(new Date(postData.publishDate));
        }
      }
      
      if (postData.categories) updateData.categories = postData.categories;
      if (postData.tags) updateData.tags = postData.tags;
      if (postData.featuredImage !== undefined) updateData.featuredImage = postData.featuredImage;
      
      // Update author name if provided
      if (postData.authorName) {
        updateData.author = {
          ...currentPost.author,
          name: postData.authorName
        };
      }
      
      if (postData.seo) {
        updateData.seo = {
          ...currentPost.seo,
          ...postData.seo
        };
      }
      
      // Update the post document
      await updateDoc(doc(db, 'blog_posts', postId), updateData);
      
      // Update slug reservation if changed
      if (newSlug !== oldSlug) {
        await updateSlugReservation(oldSlug, newSlug, postId);
      }
      
      // Update local state
      setPosts(prev => prev.map(post => 
        post.id === postId ? { ...post, ...updateData } : post
      ));
      
      return { id: postId, ...currentPost, ...updateData };
      
    } catch (err) {
      console.error('Error updating post:', err);
      setError('Failed to update post');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Deletes a blog post
   */
  const deletePost = async (postId) => {
    setLoading(true);
    setError(null);
    
    try {
      // Get the post to retrieve its slug
      const post = await fetchPost(postId);
      
      // Delete the post document
      await deleteDoc(doc(db, 'blog_posts', postId));
      
      // Release the slug
      if (post.slug) {
        await releaseSlug(post.slug);
      }
      
      // Update local state
      setPosts(prev => prev.filter(p => p.id !== postId));
      
      return true;
      
    } catch (err) {
      console.error('Error deleting post:', err);
      setError('Failed to delete post');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetches blog categories
   */
  // Fetch only categories that are actually used by at least one blog post
  const fetchCategories = async () => {
    try {
      // Get all PUBLISHED blog posts only (to respect Firestore rules)
      const publishedQuery = query(
        collection(db, 'blog_posts'),
        where('status', '==', 'published'),
        limit(1000)
      );
      
      let postsSnapshot = await getDocs(publishedQuery);
      
      // If no posts found with status='published', try published=true as fallback
      if (postsSnapshot.empty) {
        const publishedBoolQuery = query(
          collection(db, 'blog_posts'),
          where('published', '==', true),
          limit(1000)
        );
        postsSnapshot = await getDocs(publishedBoolQuery);
      }
      
      const usedCategoriesSet = new Set();
      postsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (Array.isArray(data.categories)) {
          data.categories.forEach(cat => usedCategoriesSet.add(cat));
        }
      });

      // Optionally, fetch category details from blog_categories if you want names/descriptions
      let fetchedCategories = Array.from(usedCategoriesSet).map(cat => ({ id: cat, name: cat }));

      // If you want to show friendly names from blog_categories collection:
      // const allCatSnapshot = await getDocs(collection(db, 'blog_categories'));
      // const allCatMap = {};
      // allCatSnapshot.docs.forEach(doc => { allCatMap[doc.id] = doc.data(); });
      // fetchedCategories = Array.from(usedCategoriesSet).map(cat => ({ id: cat, name: allCatMap[cat]?.name || cat }));

      setCategories(fetchedCategories);
      return fetchedCategories;
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError('Failed to fetch categories');
      throw err;
    }
  };

  /**
   * Returns accurate post counts by status using server-side aggregation.
   * Safe to call regardless of pagination limits on fetchPosts.
   */
  const fetchStats = useCallback(async () => {
    const col = collection(db, 'blog_posts');
    const [totalSnap, publishedSnap, draftSnap, scheduledSnap] = await Promise.all([
      getCountFromServer(col),
      getCountFromServer(query(col, where('status', '==', 'published'))),
      getCountFromServer(query(col, where('status', '==', 'draft'))),
      getCountFromServer(query(col, where('status', '==', 'scheduled'))),
    ]);
    return {
      totalPosts: totalSnap.data().count,
      publishedPosts: publishedSnap.data().count,
      draftPosts: draftSnap.data().count,
      scheduledPosts: scheduledSnap.data().count,
    };
  }, []);

  // Load categories on hook initialization
  useEffect(() => {
    fetchCategories();
  }, []);

  return {
    posts,
    post, // Single post state
    categories,
    loading,
    error,
    fetchPosts,
    fetchStats,
    fetchPost,
    createPost,
    updatePost,
    deletePost,
    fetchCategories,
    clearError: () => setError(null)
  };
};
